import { useEffect, useRef, useState, type ChangeEvent, type DragEvent } from 'react';
import type { MediaItem } from '../../shared/types';
import {
  allocatePreparedTasks,
  discardTask,
  makeTasks,
  prepareTasks,
  restoreUploads,
  uploadTask,
  type UploadTask,
} from '../lib/uploads';
import { CloseIcon, UploadIcon } from './Icons';

const ACCEPTED =
  'image/jpeg,image/png,image/webp,image/heic,image/heif,video/mp4,video/quicktime,.heic,.heif,.mov';

interface UploadTrayProps {
  onComplete: (item: MediaItem) => void;
}

const formatBytes = (bytes: number) => {
  if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toFixed(1)} GB`;
  if (bytes >= 1024 ** 2) return `${(bytes / 1024 ** 2).toFixed(1)} MB`;

  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
};

export function UploadTray({ onComplete }: UploadTrayProps) {
  const [tasks, setTasks] = useState<UploadTask[]>([]);
  const [open, setOpen] = useState(false);
  const [dragging, setDragging] = useState(false);
  const taskMap = useRef(new Map<string, UploadTask>());
  const controllers = useRef(new Map<string, AbortController>());
  const input = useRef<HTMLInputElement>(null);

  const reflect = (task: UploadTask) => {
    taskMap.current.set(task.clientId, task);
    setTasks(Array.from(taskMap.current.values()));
  };

  const run = async (work: UploadTask[]) => {
    const queue = [...work];
    const worker = async () => {
      let task = queue.shift();

      while (task) {
        const controller = new AbortController();
        controllers.current.set(task.clientId, controller);

        try {
          const item = await uploadTask(task, controller.signal, reflect);
          onComplete(item);
        } catch {
          // uploadTask owns the visible error or paused state.
        } finally {
          controllers.current.delete(task.clientId);
        }

        task = queue.shift();
      }
    };

    await Promise.all([worker(), worker()]);
  };

  useEffect(() => {
    let active = true;

    restoreUploads().then((restored) => {
      if (!active || restored.length === 0) return;

      for (const task of restored) reflect(task);

      setOpen(true);
      void run(restored.filter((task) => task.allocation));
    });

    return () => {
      active = false;

      for (const controller of controllers.current.values()) controller.abort();
    };
  }, []);

  const addFiles = async (files: File[]) => {
    if (files.length === 0) return;

    const next = makeTasks(files);

    for (const task of next) reflect(task);

    setOpen(true);

    try {
      await prepareTasks(next, reflect);
      await run(next.filter((task) => task.status === 'queued' && task.allocation));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Could not begin these uploads';

      for (const task of next.filter((candidate) => candidate.status !== 'error')) {
        reflect({ ...task, status: 'error', error: message });
      }
    }
  };

  const selectFiles = (event: ChangeEvent<HTMLInputElement>) => {
    void addFiles(Array.from(event.target.files ?? []));
    event.target.value = '';
  };

  const dropFiles = (event: DragEvent) => {
    event.preventDefault();
    setDragging(false);
    void addFiles(Array.from(event.dataTransfer.files));
  };

  const retry = (task: UploadTask) => {
    if (!task.allocation || task.error?.includes('expired')) {
      task.allocation = undefined;
      task.originalOffset = 0;
      task.previewOffset = 0;
      task.uploadedBytes = 0;
      task.status = 'queued';
      task.error = undefined;

      void allocatePreparedTasks([task], reflect)
        .then(() => run([task]))
        .catch((error) => {
          reflect({
            ...task,
            status: 'error',
            error: error instanceof Error ? error.message : 'Could not restart upload',
          });
        });
      return;
    }

    void run([task]);
  };

  const remove = async (task: UploadTask) => {
    controllers.current.get(task.clientId)?.abort();
    taskMap.current.delete(task.clientId);
    setTasks(Array.from(taskMap.current.values()));
    await discardTask(task);
  };

  const activeTasks = tasks.filter((task) => task.status !== 'complete');
  const completedCount = tasks.length - activeTasks.length;
  const totalBytes = tasks.reduce((sum, task) => sum + task.totalBytes, 0);
  const uploadedBytes = tasks.reduce((sum, task) => sum + task.uploadedBytes, 0);
  const aggregate = totalBytes ? Math.min(100, (uploadedBytes / totalBytes) * 100) : 0;

  return (
    <>
      <input
        ref={input}
        className="sr-only"
        type="file"
        accept={ACCEPTED}
        multiple
        onChange={selectFiles}
        aria-label="Choose photos and videos"
      />

      <button
        className="upload-fab"
        type="button"
        onClick={() => (tasks.length ? setOpen(true) : input.current?.click())}
      >
        <UploadIcon />
        <span>Upload</span>
        {activeTasks.length > 0 && <strong>{activeTasks.length}</strong>}
      </button>

      {open && (
        <aside className="upload-tray" aria-label="Uploads">
          <header className="upload-tray__header">
            <div>
              <p className="eyebrow">Uploads</p>
              <h2>
                {activeTasks.length
                  ? `${activeTasks.length} ${activeTasks.length === 1 ? 'item' : 'items'} in progress`
                  : completedCount
                    ? `${completedCount} uploaded`
                    : 'Add trip memories'}
              </h2>
            </div>
            <button className="icon-button" type="button" onClick={() => setOpen(false)} aria-label="Close uploads">
              <CloseIcon />
            </button>
          </header>

          {tasks.length > 0 && (
            <div className="upload-tray__aggregate">
              <span style={{ width: `${aggregate}%` }} />
            </div>
          )}

          <button
            className={`drop-zone${dragging ? ' drop-zone--active' : ''}`}
            type="button"
            onClick={() => input.current?.click()}
            onDragEnter={(event) => {
              event.preventDefault();
              setDragging(true);
            }}
            onDragOver={(event) => event.preventDefault()}
            onDragLeave={() => setDragging(false)}
            onDrop={dropFiles}
          >
            <UploadIcon />
            <span>Choose or drop photos &amp; videos</span>
          </button>

          <div className="upload-list" aria-live="polite">
            {tasks.map((task) => {
              const progress = task.totalBytes
                ? Math.min(100, (task.uploadedBytes / task.totalBytes) * 100)
                : 0;

              return (
                <article className="upload-item" key={task.clientId}>
                  {task.file.type.startsWith('image/') ? (
                    <img src={task.localUrl} alt="" />
                  ) : (
                    <div className="upload-item__video" aria-hidden="true">
                      MOV
                    </div>
                  )}
                  <div className="upload-item__body">
                    <p title={task.file.name}>{task.file.name}</p>
                    <small>
                      {task.status === 'preparing' && 'Preparing display copy…'}
                      {task.status === 'queued' && 'Waiting…'}
                      {task.status === 'uploading' &&
                        `${Math.round(progress)}% · ${formatBytes(task.file.size)}`}
                      {task.status === 'paused' && 'Paused'}
                      {task.status === 'complete' && 'Uploaded'}
                      {task.status === 'error' && task.error}
                    </small>
                    <span className="upload-item__progress">
                      <span style={{ width: `${task.status === 'complete' ? 100 : progress}%` }} />
                    </span>
                  </div>
                  <div className="upload-item__actions">
                    {(task.status === 'error' || task.status === 'paused') && (
                      <button type="button" onClick={() => retry(task)}>
                        Retry
                      </button>
                    )}
                    {task.status !== 'complete' && task.status !== 'error' && (
                      <button type="button" onClick={() => controllers.current.get(task.clientId)?.abort()}>
                        Pause
                      </button>
                    )}
                    <button type="button" onClick={() => void remove(task)}>
                      {task.status === 'complete' ? 'Clear' : 'Cancel'}
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        </aside>
      )}
    </>
  );
}
